import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from "@angular/core";
import {
  BM_MULTI_ALLOCATION_COLS,
  BM_MULTI_ALLOCATION_COL_ACCOUNT,
  BM_MULTI_ALLOCATION_COL_AMOUNT,
  BM_MULTI_ALLOCATION_COL_FIXED_CCY,
  BM_MULTI_ALLOCATION_COL_INTENT,
  BM_MULTI_ALLOCATION_COL_PRODUCT,
  BM_MULTI_ALLOCATION_COL_SELECTION,
  BM_MULTI_ALLOCATION_COL_TENOR,
  BM_MULTI_ALLOCATION_COL_VALUE_DATE,
} from "./oe-panel-bmark-allocation-grid.constants";

import { OEBmarkEntryComponent } from "./oe-panel-bmark-entry.component";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { OEAllocationBulkService } from "../services/oe-allocation-bulk.service";
import { DfAutoComplete } from "../../bfx-detail-fill/editors/DfAutoComplete";
import {
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  GridOptions,
  TabToNextCellParams,
} from "ag-grid-community";
import { ICTenorValueDateModel } from "./controls/models/bfx-interface-controls.models";
import { DfAmountSpinner } from "../../bfx-detail-fill/editors/DfAmountSpinner";
import { DfCalendarComponent } from "../../bfx-detail-fill/editors/DfCalendar";
import { OEPanelBmarkModel } from "../data/models/oe-panel-bmark.model";
import { BehaviorSubject, Subject, Subscription } from "rxjs";

import { LoggerService } from "@bfx/logger";
import { TenorDfAutoComplete } from "../../bfx-detail-fill/editors/TenorDfAutoComplete";
import { OECoordinatorService } from "../services/oe-coordinator.service";
import { OECommandEventModel } from "../data/models/oe-command-event.model";
import { runInZone } from "shared/helper-services/run-in-zone";
import { OENCommand } from "../data/enums/oe-command.enum";
import { OEModelLockingService } from "../services/model/oe-model-locking.service";

import { OENInputControl } from "../data/enums/oe-input-control.enum";
import { OEInputModel } from "../data/models/oe-input.model";
import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";

const logger = LoggerService.for(__dirname);

const UNBULK_ALLOCATIONS_FN = (a) => {
  if (
    a instanceof CubiqTS.AllocationRequestDataImpl &&
    a.settleTenor != null &&
    a.settleTenor != CubiqTS.Tenor.EMPTY &&
    a.settleTenor != CubiqTS.Tenor.ALL
  ) {
    a.settleDate = null;
  }
  return a;
};

const IS_INVALID_SETTLE_DATE_FN = (a, minDate: Date) => {
  let settleDate;

  if (minDate == null || minDate instanceof Date === false) {
    return false;
  }

  if (
    a instanceof CubiqTS.AllocationRequestDataImpl &&
    a.settleDate != null &&
    a.settleDate instanceof CubiqTS.StandardDateImpl
  ) {
    settleDate = new Date(0);
    settleDate.setFullYear(a.settleDate.year);
    settleDate.setMonth(a.settleDate.month - 1);
    settleDate.setDate(a.settleDate.day);
  } else if (
    a != null &&
    typeof a === "object" &&
    a.settleDate != null &&
    a.settleDate instanceof Date
  ) {
    settleDate = a.settleDate;
  } else {
    return false;
  }

  return settleDate.valueOf() < minDate.valueOf();
};

@Component({
  selector: "oe-panel-bmark-allocation-grid",
  templateUrl: "./html/oe-panel-bmark-allocation-grid.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OEBmarkAllocationGridComponent implements OnDestroy {
  public control = {
    lockAllFg: false,
  };

  public data: Partial<OEPanelBmarkModel> = {
    currencyPair: null,
    valueDate: null,
    account: null,
    orderAmount: null,
    clientIntent: null,
    fixingDate: null,
  };
  entryPanelCoordinatorSubscription: Subscription;
  actionsCoordinatorSubscription: Subscription;
  isEditInProgress: boolean = false;
  isInvalidSubmitAsUnbulked: boolean = false;
  selectionCount: number = 0;

  private _calendar: CubiqTS.CurrencyPairCalendarImpl;
  holidays: Date[];
  allocationGridLoadSubscription: Subscription;

  @ViewChild("grid", { read: ElementRef }) grid: ElementRef;

  get calendar() {
    return this._calendar;
  }

  @Input("calendar") set calendar(value) {
    let holidays = [];

    if (value instanceof CubiqTS.CurrencyPairCalendarImpl && Array.isArray(value.holidays)) {
      holidays = value.holidays.map((a) => {
        return new Date(a.date.year, a.date.month - 1, a.date.day);
      });
    }
    this._calendar = value;
    this.holidays = holidays;
    this.cd.markForCheck();
  }

  get currencyPair() {
    return this.data.currencyPair;
  }

  @Input("currencyPair") set currencyPair(value) {
    this.data.currencyPair = value;
    this.data = { ...this.data };
    this.cd.markForCheck();
  }

  get fixedCcy() {
    return this.data.fixedCcy;
  }

  @Input("fixedCcy") set fixedCcy(value) {
    this.data.fixedCcy = value;
    this.data = { ...this.data };

    this.gridAllocations = this.gridAllocations.map((a) => {
      a.fixedCcy = value.fixedCcy;
      return a;
    });
    if (this.singleAllocations != null) {
      this.singleAllocations.forEach((alloc) => (alloc.fixedCcy = value.fixedCcy));
    }
    this.cd.markForCheck();
  }

  get valueDate() {
    return this.data.valueDate;
  }

  @Input("valueDate") set valueDate(value) {
    this.data.valueDate = value;
    this.data = { ...this.data };
  }

  get fixingDate() {
    return this.data.fixingDate;
  }

  @Input("fixingDate") set fixingDate(value) {
    this.data.fixingDate = value;
    this.minDateSubject.next(value.value);

    if (Array.isArray(this.gridAllocations)) {
      if (this.parent.isBulked) {
        this.edit();
      }

      this.gridAllocations = this.gridAllocations.map((a) => {
        if (
          a instanceof CubiqTS.AllocationRequestDataImpl &&
          IS_INVALID_SETTLE_DATE_FN(a, value.value)
        ) {
          a.settleDate = null;
          this.isInvalidSubmitAsUnbulked = true;
        }
        return a;
      });
    }
    this.cd.markForCheck();
  }

  /**
   * After a change to one or more value dates check to see if we can remove the invalid flag
   * where all required value dates are specified and valid
   */
  private checkAllocationsValid() {
    const fixingDate = this.fixingDate?.value;
    if (Array.isArray(this.gridAllocations) && fixingDate !== null) {
      this.isInvalidSubmitAsUnbulked = this.gridAllocations.some((allocation) => {
        if (allocation instanceof CubiqTS.AllocationRequestDataImpl) {
          return (
            allocation.settleTenor == null &&
            (allocation.settleDate == null || IS_INVALID_SETTLE_DATE_FN(allocation, fixingDate))
          );
        }
        return false;
      });
    }
    this.cd.markForCheck();
  }

  get account() {
    return this.data.account;
  }

  @Input("account") set account(value) {
    this.data.account = value;
    this.data = { ...this.data };
    this.cd.markForCheck();
  }

  get orderAmount() {
    return this.data.orderAmount;
  }

  @Input("orderAmount") set orderAmount(value) {
    this.data.orderAmount = value;
    this.data = { ...this.data };
    this.cd.markForCheck();
  }

  get clientIntent() {
    return this.data.clientIntent;
  }

  @Input("clientIntent") set clientIntent(value) {
    this.data.clientIntent = value;
    this.data = { ...this.data };
    this.cd.markForCheck();
  }

  public singleAllocations: Partial<
    CubiqTS.FixingOrderRequestData | CubiqTS.AllocationRequestData
  >[] = [];

  public gridAllocations: Partial<
    CubiqTS.FixingOrderRequestData | CubiqTS.AllocationRequestData
  >[] = [];

  public allocations: CubiqTS.AllocationRequestData[] = [];
  public farAllocations: CubiqTS.AllocationRequestData[] = [];

  @Output("allocations")
  public allocationsSubject = new BehaviorSubject<CubiqTS.AllocationRequestData[]>([]);

  @Output("farAllocations")
  public farAllocationsSubject = new BehaviorSubject<CubiqTS.AllocationRequestData[]>([]);

  @Output("product")
  public productSubject = new BehaviorSubject<CubiqTS.Product | null>(null);

  @Output("orderIntent")
  public orderIntentSubject = new BehaviorSubject<CubiqTS.OrderIntent | null>(null);

  @Output("fixedAmount")
  public fixedAmountSubject = new BehaviorSubject<CubiqTS.BigNumber | null>(null);

  @Output("fixedCcy")
  public fixedCcySubject = new BehaviorSubject<string | null>(null);

  @Output("swapFixedAmount")
  public swapFixedAmountSubject = new BehaviorSubject<CubiqTS.BigNumber | null>(null);

  @Output("valueDate")
  public valueDateSubject = new BehaviorSubject<{
    tenor: CubiqTS.Tenor;
    valueDate: CubiqTS.StandardDate;
  } | null>(null);

  @Output("swapTenor")
  public swapTenorSubject = new BehaviorSubject<CubiqTS.Tenor | null>(null);

  @Output("swapValueDate")
  public swapValueDateSubject = new BehaviorSubject<CubiqTS.StandardDate | null>(null);

  @Output("accountCode")
  public accountCodeSubject = new BehaviorSubject<string | null>(null);

  public get hasAllocations(): boolean {
    return this.gridAllocations.length > 0;
  }

  public editingStoppedSubject = new Subject<void>();
  public minDateSubject = new BehaviorSubject<Date>(new Date());
  public isEditable: boolean = true;

  private clickListenerInstalled: boolean = false;

  public allocationModes = [
    {
      label: "Single",
      value: false,
    },
    {
      label: "Multiple",
      value: true,
    },
  ];
  public allocationMode: boolean = false;

  private _gridOptions = <GridOptions>{
    singleClickEdit: true,
    onSelectionChanged: (event) => {
      this.selectionCount = event.api.getSelectedRows().length;
      this.cd.markForCheck();
    },
    rowSelection: "multiple",
    onCellEditingStarted: function (_: CellEditingStartedEvent) {
      this.addClickListener();
      this.isEditInProgress = true;
      this.cd.markForCheck();
    }.bind(this),
    onCellEditingStopped: (_: CellEditingStoppedEvent) => {
      this.isEditInProgress = false;
      this.checkAllocationsValid();
      this.cd.markForCheck();
    },
    tabToNextCell: (params: TabToNextCellParams) => {
      if (params.editing && params.nextCellPosition === null) {
        this.editingStoppedSubject.next();
        return null;
      } else if (
        params.nextCellPosition &&
        (params.nextCellPosition.column.getColDef().field === BM_MULTI_ALLOCATION_COL_TENOR ||
          params.nextCellPosition.column.getColDef().field === BM_MULTI_ALLOCATION_COL_VALUE_DATE)
      ) {
        let rowIndex = params.previousCellPosition.rowIndex;
        let node;
        this.gridOptions.api.forEachNode((n, i) => {
          if (rowIndex === i) node = n;
        });

        if (params.backwards) {
          params.nextCellPosition.column = this.gridOptions.columnApi.getColumn(
            BM_MULTI_ALLOCATION_COL_AMOUNT,
          );
          return params.nextCellPosition;
        } else if (
          node.data[BM_MULTI_ALLOCATION_COL_TENOR] == null &&
          params.nextCellPosition.column.getColDef().field === BM_MULTI_ALLOCATION_COL_TENOR
        ) {
          params.nextCellPosition.column = this.gridOptions.columnApi.getColumn(
            BM_MULTI_ALLOCATION_COL_VALUE_DATE,
          );
          return params.nextCellPosition;
        } else {
          return params.nextCellPosition;
        }
      } else {
        return params.nextCellPosition;
      }
    },
    context: {
      getAutoCompleteValues: this.getAutoCompleteValues.bind(this),
      isAutoCompleteSkipSuggestionsSort: this.isAutoCompleteSkipSuggestionsSort.bind(this),
      setEditedValue: this.setEditedValue.bind(this),
      isAutoCompleteValueLabel: () => false,
      // TODO shorten
      isAutoCompleteOnInvalidReselectOldValue: () => true,
      // for DfAmountSpinner
      isCellEditingEnabled: () => {
        if (this.parent.isBulked) {
          return false;
        }
        return true;
      },
      minDate: this.minDateSubject,
      getCurrency: (params) => {
        return params.data[BM_MULTI_ALLOCATION_COL_FIXED_CCY];
      },
      dfCalendarNullFields: new Set([BM_MULTI_ALLOCATION_COL_TENOR]),
      dfCalendarHolidays: () => this.holidays,
      editingStoppedSubject: this.editingStoppedSubject,
      isBulked: () => this.parent.isBulked,
      getAutoCompleteSelectedValue: this.getAutoCompleteSelectedValue.bind(this),
    },
  };

  public get gridOptions() {
    return this._gridOptions;
  }

  public set gridOptions(value) {
    this._gridOptions = value;
  }

  public gridColumns = BM_MULTI_ALLOCATION_COLS;

  public frameworkComponents = {
    [BM_MULTI_ALLOCATION_COL_FIXED_CCY]: DfAutoComplete,
    [BM_MULTI_ALLOCATION_COL_INTENT]: DfAutoComplete,
    [BM_MULTI_ALLOCATION_COL_TENOR]: TenorDfAutoComplete,
    [BM_MULTI_ALLOCATION_COL_ACCOUNT]: DfAutoComplete,
    [BM_MULTI_ALLOCATION_COL_AMOUNT]: DfAmountSpinner,
    [BM_MULTI_ALLOCATION_COL_VALUE_DATE]: DfCalendarComponent,
  };

  constructor(
    public parent: OEBmarkEntryComponent,
    public bulkService: OEAllocationBulkService,
    private coordinator: OECoordinatorService,
    private locking: OEModelLockingService,
    public cd: ChangeDetectorRef,
    public ngZone: NgZone,
    public el: ElementRef,
  ) {
    this.initialize();
  }

  public onGridReady(params) {
    if (this.gridOptions != null) {
      this.gridOptions.api = params.api;
      this.gridOptions.columnApi = params.columnApi;
    } else {
      logger.out.warn(`${this.constructor.name}.gridOptions.onGridReady() gridOptions null`);
    }
  }

  protected addClickListener(): void {
    if (NativeDesktopSupport.isNativeDesktopContainer() && !this.clickListenerInstalled) {
      this.clickListenerInstalled = true;
      let container = this.grid.nativeElement.getElementsByClassName("ag-center-cols-container");

      this.el.nativeElement.ownerDocument.body.addEventListener("click", (e) => {
        // Container can be null when closing the window
        if (container.length > 0 && !container[0].contains(e.target)) {
          this.gridOptions.api.stopEditing();
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.entryPanelCoordinatorSubscription != null) {
      this.entryPanelCoordinatorSubscription.unsubscribe();
      this.entryPanelCoordinatorSubscription = null;
    }

    if (this.actionsCoordinatorSubscription != null) {
      this.actionsCoordinatorSubscription.unsubscribe();
      this.actionsCoordinatorSubscription = null;
    }
  }

  protected initialize(): void {
    this.subscribeToCoordinator();
    this.editingStoppedSubject.subscribe(() => {
      this.isEditInProgress = false;
      // After editing a value date, check the state of all allocations
      this.checkAllocationsValid();
    });

    this.allocationGridLoadSubscription = this.parent.allocationGridLoadSubject.subscribe(
      (value) => {
        this.gridAllocations = Array.isArray(value.allocations)
          ? value.allocations.map(UNBULK_ALLOCATIONS_FN)
          : [];
        this.isInvalidSubmitAsUnbulked = false;
        this.allocationMode = this.gridAllocations.length > 0;
        if (this._gridOptions?.api && this.gridAllocations.length) {
          this._gridOptions.api.sizeColumnsToFit();
        }
        let lockProfile = this.locking.createLockProfileForBulking(
          this.parent.refId,
          this.parent.isBulking,
        );

        this.coordinator.cmdLoadInterfaceControlLockingProfile(this.parent.refId, lockProfile);

        this.cd.markForCheck();
      },
    );
  }

  subscribeToCoordinator() {
    this.entryPanelCoordinatorSubscription = this.coordinator.entryPanelCmd$
      .pipe(runInZone(this.ngZone))
      .subscribe(($event: OECommandEventModel) => {
        if ($event.ref === this.parent.refId) {
          switch ($event.cmd) {
            case OENCommand.RESET_ALL:
              this.isEditable = true;
              this.isInvalidSubmitAsUnbulked = false;
              this.reset();
              break;
            case OENCommand.LOCK_ALL:
              this.lockAll();
              break;
            case OENCommand.UNLOCK_ALL:
              this.unlockAll();
              break;
            case OENCommand.STATE_ORDER_PROCESSING:
              // needs to be expanded for modify
              this.isEditable = false;
              break;
            case OENCommand.INVALID_INPUT:
              if (
                $event.data != null &&
                $event.data instanceof OEInputModel &&
                OENInputControl.ALLOCATIONS === $event.data.input
              ) {
                this.isInvalidSubmitAsUnbulked = true;
              }
              break;
            case OENCommand.UNBULK:
              if ($event.data !== OENInputControl.ALLOCATIONS) {
                this.edit();
              }
            default:
              break;
          }
        }
      });

    this.actionsCoordinatorSubscription = this.coordinator.actionsPanelCmd$
      .pipe(runInZone(this.ngZone))
      .subscribe(($event: OECommandEventModel) => {
        if ($event.ref === this.parent.refId) {
          switch ($event.cmd) {
            case OENCommand.STATE_ORDER_PROCESSING:
              // needs to be expanded for modify
              this.isEditable = false;
              break;
            default:
              break;
          }
        }
      });
  }

  reset(allocationRequestData?: Partial<CubiqTS.AllocationRequestData>) {
    this.gridAllocations = [];
    this.allocations = [];
    this.farAllocations = [];
    this.allocationMode = false;

    this.allocationsSubject.next(this.allocations);
    this.farAllocationsSubject.next(this.farAllocations);
    if (allocationRequestData != null) {
      let qty = allocationRequestData.allocationQty as unknown;
      if (qty instanceof CubiqTS.BigNumber) {
        this.fixedAmountSubject.next(qty);
      } else if (typeof qty === "string") {
        this.fixedAmountSubject.next(new CubiqTS.BigNumber(allocationRequestData.allocationQty));
      } else if (typeof qty === "number") {
        this.fixedAmountSubject.next(new CubiqTS.BigNumber(allocationRequestData.allocationQty));
      }
      if (allocationRequestData.allocationIntent != null) {
        this.orderIntentSubject.next(allocationRequestData.allocationIntent);
      }
      if (allocationRequestData.fixedCcy != null) {
        this.fixedCcySubject.next(allocationRequestData.fixedCcy);
      }
      if (allocationRequestData.settleDate != null || allocationRequestData.settleTenor != null) {
        this.valueDateSubject.next({
          tenor: allocationRequestData.settleTenor,
          valueDate: allocationRequestData.settleDate,
        });
      }
      if (allocationRequestData.accountCode) {
        this.accountCodeSubject.next(allocationRequestData.accountCode);
      }
    } else if (this.data.orderAmount.value instanceof CubiqTS.BigNumber) {
      this.fixedAmountSubject.next(this.data.orderAmount.value);
    }

    // Need to reset the product back to single allocation product
    if (typeof allocationRequestData?.settleTenor === "number") {
      this.productSubject.next(
        allocationRequestData?.settleTenor === CubiqTS.Tenor.SPOT
          ? CubiqTS.Product.SPT
          : CubiqTS.Product.FWD,
      );
    } else {
      this.productSubject.next(CubiqTS.Product.SPT);
    }
    this.parent.isBulked = false;
    this.parent.isBulking = false;

    let lockProfile = this.locking.createLockProfileForBulking(
      this.parent.refId,
      this.parent.isBulked,
    );

    this.coordinator.cmdUnbulk(this.parent.refId, OENInputControl.ALLOCATIONS);
    this.coordinator.cmdLoadInterfaceControlLockingProfile(this.parent.refId, lockProfile);

    this.cd.markForCheck();
  }

  isAutoCompleteSkipSuggestionsSort(params): boolean {
    switch (params.colDef.field) {
      case BM_MULTI_ALLOCATION_COL_TENOR: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  public getAutoCompleteValues(params): Array<any> {
    let values: Array<any>;

    switch (params.colDef.field) {
      case BM_MULTI_ALLOCATION_COL_ACCOUNT: {
        values =
          this.data.account != null && Array.isArray(this.data.account.dataset)
            ? this.data.account.dataset.map((v) => {
                return { label: v.id, value: v.id };
              })
            : [];
        break;
      }
      case BM_MULTI_ALLOCATION_COL_INTENT: {
        values = [
          { label: "BUY", value: CubiqTS.OrderIntent.BUY },
          { label: "SELL", value: CubiqTS.OrderIntent.SELL },
        ];
        break;
      }
      case BM_MULTI_ALLOCATION_COL_FIXED_CCY: {
        let currencyPair = this.currencyPair;
        if (currencyPair != null && typeof currencyPair.product === "string") {
          let base = currencyPair.product.substring(0, 3);
          let nonBase = currencyPair.product.substring(3, 6);

          values = [
            { label: base, value: base },
            { label: nonBase, value: nonBase },
          ];
        }
        break;
      }
      case BM_MULTI_ALLOCATION_COL_TENOR: {
        const valueDate = this.valueDate;
        values = [];

        if (
          valueDate != null &&
          valueDate.dataset != null &&
          valueDate.dataset instanceof CubiqTS.CurrencyPairCalendarImpl &&
          Array.isArray(valueDate.dataset.tenorToValueDateSet)
        ) {
          valueDate.dataset.tenorToValueDateSet.forEach((element) => {
            values.push(
              new ICTenorValueDateModel(
                element.tenor,
                new Date(
                  element.valueDate.year,
                  element.valueDate.month - 1,
                  element.valueDate.day,
                ),
              ),
            );
          });
        }
        values.sort((a, b) => {
          return a.valueDate > b.valueDate ? 1 : -1;
        });
        break;
      }
      default: {
        values = [];
        break;
      }
    }

    return values;
  }

  lockAll() {
    this.control.lockAllFg = true;
    this.cd.markForCheck();
  }

  unlockAll() {
    this.control.lockAllFg = false;
    this.cd.markForCheck();
  }

  public setEditedValue(value: any, params: any): void {
    switch (params.colDef.field) {
      case BM_MULTI_ALLOCATION_COL_TENOR: {
        if (value != null && value instanceof ICTenorValueDateModel) {
          params.value = value.value;
        } else if (value != null) {
          params.value = value;
        }
        break;
      }
      default: {
        if (value != null) {
          params.value = value;
        }
      }
    }
  }

  single() {
    let selected =
      this.gridOptions != null && this.gridOptions.api != null
        ? this.gridOptions.api.getSelectedRows()
        : [];
    this.parent.isBulking = false;
    this.parent.isBulked = false;
    this.isInvalidSubmitAsUnbulked = false;
    const oldAllocations = this.gridAllocations;
    this.gridAllocations = [];
    this.singleAllocations = [];

    let columnState = this.gridOptions.columnApi.getColumnState();
    columnState.every((col) => {
      if (col.colId === BM_MULTI_ALLOCATION_COL_PRODUCT) {
        col.hide = true;
      } else if (col.colId === BM_MULTI_ALLOCATION_COL_SELECTION) {
        col.hide = false;
      }
    });

    this.gridOptions.columnApi.setColumnState(columnState);

    if (selected.length > 0 || oldAllocations?.length > 0) {
      let primeAllocation;
      if (selected.length > 0) {
        primeAllocation = selected[0];
      } else {
        // Need to ignore any FixingOrderRequestData present after bulking
        primeAllocation = oldAllocations.find((allocation) =>
          allocation.hasOwnProperty("allocationIntent"),
        );
      }
      this.reset(primeAllocation);
    } else {
      this.reset();
    }
    this.cd.markForCheck();
  }

  edit() {
    if (this.parent.isBulked === false) {
      return;
    }
    this.parent.isBulked = false;
    this.isEditable = true;
    this.gridAllocations = this.singleAllocations;
    this.singleAllocations = [];
    let columnState = this.gridOptions.columnApi.getColumnState();
    columnState.some((col) => {
      if (col.colId === BM_MULTI_ALLOCATION_COL_PRODUCT) {
        col.hide = true;
      } else if (col.colId === "selection") {
        col.hide = false;
      }

      return false;
    });

    this.gridAllocations = this.gridAllocations.map(UNBULK_ALLOCATIONS_FN);
    this.gridOptions.columnApi.setColumnState(columnState);
    this.cd.markForCheck();
  }

  bulk() {
    if (this.parent.isBulked) {
      return;
    }

    this.isInvalidSubmitAsUnbulked = false;
    const requestAllocations = this.getAllocationsForBulk();
    this.bulkService
      .getBulkReport(this.parent.refId, requestAllocations, this.parent.operationMode)
      .then(
        (report) => {
          if (
            report != null &&
            report instanceof CubiqTS.StandardReportImpl &&
            Array.isArray(report.results) &&
            report.results.length > 0
          ) {
            let fixingOrderData = null;
            this.singleAllocations = [...this.gridAllocations];
            this.gridAllocations = [];
            let allocations = [];
            let farAllocations = [];

            let gridAllocations = report.results.flatMap((request) => {
              if (request == null || request.fixingOrderData == null) return [];

              fixingOrderData = request.fixingOrderData;
              allocations = [
                ...allocations,
                ...(Array.isArray(request.fixingOrderData.allocations)
                  ? request.fixingOrderData.allocations
                  : []),
              ];
              farAllocations = [
                ...farAllocations,
                ...(Array.isArray(request.fixingOrderData.farAllocations)
                  ? request.fixingOrderData.farAllocations
                  : []),
              ];
              return [
                ...(Array.isArray(request.fixingOrderData.allocations)
                  ? request.fixingOrderData.allocations
                  : []),
                ...(Array.isArray(request.fixingOrderData.farAllocations)
                  ? request.fixingOrderData.farAllocations
                  : []),
              ];
            });

            this.parent.isBulked = fixingOrderData != null;

            this.productSubject.next(
              fixingOrderData != null && fixingOrderData.product != null
                ? fixingOrderData.product
                : null,
            );

            this.orderIntentSubject.next(
              fixingOrderData != null && fixingOrderData.clientIntent != null
                ? fixingOrderData.clientIntent
                : null,
            );

            this.fixedAmountSubject.next(
              fixingOrderData != null && fixingOrderData.fixedAmount != null
                ? new CubiqTS.BigNumber(fixingOrderData.fixedAmount)
                : null,
            );

            this.fixedCcySubject.next(
              fixingOrderData != null && fixingOrderData.fixedCcy != null
                ? fixingOrderData.fixedCcy
                : null,
            );

            this.valueDateSubject.next({
              tenor:
                fixingOrderData != null && fixingOrderData.tenor != null
                  ? fixingOrderData.tenor
                  : null,
              valueDate:
                fixingOrderData != null && fixingOrderData.valueDate != null
                  ? fixingOrderData.valueDate
                  : null,
            });

            this.swapTenorSubject.next(
              fixingOrderData != null && fixingOrderData.swapTenor != null
                ? fixingOrderData.swapTenor
                : null,
            );

            this.swapValueDateSubject.next(
              fixingOrderData != null && fixingOrderData.swapValueDate != null
                ? fixingOrderData.swapValueDate
                : null,
            );

            this.swapFixedAmountSubject.next(
              fixingOrderData != null && fixingOrderData.swapFixedAmount != null
                ? fixingOrderData.swapFixedAmount
                : null,
            );

            this.allocationsSubject.next(allocations);
            this.farAllocationsSubject.next(farAllocations);

            if (!this.parent.isBulked) {
              this.gridAllocations = [...this.singleAllocations];
              this.edit();
              this.isInvalidSubmitAsUnbulked = true;
              // failed to get a bulk report. revert
              return;
            }

            this.gridAllocations = [fixingOrderData, ...gridAllocations];
            let columnState = this.gridOptions.columnApi.getColumnState();
            columnState.some((col) => {
              if (col.colId === BM_MULTI_ALLOCATION_COL_PRODUCT) {
                col.hide = false;
              } else if (col.colId === BM_MULTI_ALLOCATION_COL_SELECTION) {
                col.hide = true;
              }

              return false;
            });

            this.isEditable = true;
            this.gridOptions.columnApi.setColumnState(columnState);
            this.coordinator.cmdBulked(this.parent.refId, OENInputControl.ALLOCATIONS);
          } else {
            this.edit();
            this.isInvalidSubmitAsUnbulked = true;
          }
          this.cd.markForCheck();
        },
        (errors: Array<OEInputModel>) => {
          this.edit();
          this.isInvalidSubmitAsUnbulked = true;
          if (errors?.length > 0) {
            this.coordinator.cmdInvalidInput(this.parent.refId, errors);
          }
          this.cd.markForCheck();
        },
      );
  }

  private getAllocationsForBulk(): Partial<CubiqTS.AllocationRequestData>[] {
    return [...this.gridAllocations].map((a: CubiqTS.AllocationRequestData) => {
      if ((a.allocationQty as unknown) instanceof CubiqTS.BigNumber) {
        a.allocationQty = a.allocationQty.toString();
      }
      if ((a.settleDate as unknown) instanceof CubiqTS.StandardDateImpl) {
        a.settleTenor = null;
      }
      return a;
    });
  }

  get isAddAllocationDisabled(): boolean {
    return (
      this.data.clientIntent.value == null ||
      this.data.currencyPair.model == null ||
      this.data.fixedCcy.fixedCcy == null ||
      this.data.orderAmount.value == null ||
      (this.data.account.value == null && this.data.account.dataset == null) ||
      this.data.valueDate.value == null ||
      this.control.lockAllFg === true
    );
  }

  addAllocation() {
    if (this.gridOptions?.api) {
      this.gridOptions.api.stopEditing();
    }
    if (this.isAddAllocationDisabled) {
      return;
    }
    this.cd.markForCheck();
    this.parent.isBulking = true;

    let lockProfile = this.locking.createLockProfileForBulking(
      this.parent.refId,
      this.parent.isBulking,
    );

    this.coordinator.cmdLoadInterfaceControlLockingProfile(this.parent.refId, lockProfile);
    this.coordinator.cmdBulking(this.parent.refId, OENInputControl.ALLOCATIONS);

    let selected =
      this.gridOptions != null && this.gridOptions.api != null
        ? this.gridOptions.api.getSelectedRows()
        : [];
    let addClones = this.gridAllocations
      .filter((a) => {
        return selected.includes(a);
      })
      .map((a) => Object.assign({}, a));

    this.gridAllocations = [
      ...this.gridAllocations,
      ...(addClones.length > 0
        ? addClones
        : [
            {
              [BM_MULTI_ALLOCATION_COL_INTENT]: this.data.clientIntent.value,
              [BM_MULTI_ALLOCATION_COL_FIXED_CCY]: this.data.fixedCcy.fixedCcy,
              [BM_MULTI_ALLOCATION_COL_AMOUNT]: this.data.orderAmount.value,
              [BM_MULTI_ALLOCATION_COL_ACCOUNT]:
                this.data.account != null && this.data.account.value != null
                  ? this.data.account.value.id
                  : null,
              [BM_MULTI_ALLOCATION_COL_TENOR]: this.data.valueDate.value.tenor,
              [BM_MULTI_ALLOCATION_COL_VALUE_DATE]:
                this.data.valueDate.value.tenor == null
                  ? (this.data.valueDate.value.valueDate as any)
                  : null,
            },
          ]),
    ];

    this.allocationsSubject.next(this.allocations);
    this.farAllocationsSubject.next(this.farAllocations);
    this._gridOptions.api.sizeColumnsToFit();
  }

  removeAllocation() {
    let selected = this.gridOptions.api.getSelectedRows();
    if (selected.length === 0) {
      return;
    }

    this.cd.markForCheck();

    this.gridAllocations = this.gridAllocations.filter((a) => {
      return !selected.includes(a);
    });

    if (this.gridAllocations.length === 0) {
      this.reset(selected[0]);
    }
  }

  public getAutoCompleteSelectedValue(params: any, provider: { label: string; value: any }[]): any {
    let data = params.data;

    switch (params.colDef.field) {
      case BM_MULTI_ALLOCATION_COL_INTENT:
      case BM_MULTI_ALLOCATION_COL_ACCOUNT:
      case BM_MULTI_ALLOCATION_COL_TENOR:
        let value = data[params.colDef.field];
        let item = Array.isArray(provider) ? provider.find((item) => item.value === value) : null;
        return item;
      default:
        return null;
    }
  }

  onOptionClick($event) {
    // Check we have now clicked on the active mode as we don't want to do any allocation processing
    // if value is unchanged - Note: Click is emitted first by p-selectbutton, then model is updated
    if ($event.option.value !== this.allocationMode) {
      if ($event.option.value === true && this.isAddAllocationDisabled === false) {
        this.addAllocation();
      } else if ($event.option.value === false && this.hasAllocations) {
        this.single();
      }
    }
  }
}
